import { type MeResponse, SiteReportDraft, type SiteReportSnapshot, SiteVerification } from '@epc/contracts';
import { z } from 'zod';
import { ApiError, api } from '../../lib/api';
import type { SiteTransport, SiteTransportResult } from './offline/queue';
import type { OfflineScope } from './offline/store';

export const reportResponse = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  authorId: z.uuid(),
  clientId: z.uuid(),
  revision: z.number().int().positive(),
  state: z.enum(['draft', 'submitted', 'verified', 'returned', 'rejected', 'accepted']),
  draft: SiteReportDraft,
  verification: z.array(SiteVerification).nullable(),
  accepted: z.array(SiteVerification).nullable(),
  updatedAt: z.string(),
  decision: z
    .object({
      actorId: z.uuid(),
      action: z.enum(['save', 'submit', 'verify', 'return', 'reject', 'accept', 'reopen']),
      reason: z.string().nullable(),
    })
    .nullable(),
  remarkManifest: z.null().optional(),
});

type Request = (
  path: string,
  options?: {
    method?: 'GET' | 'POST' | 'PATCH';
    body?: unknown;
    idempotencyKey?: string;
    expectedIdentity?: Pick<OfflineScope, 'userId' | 'tenantId'>;
    signal?: AbortSignal;
  },
) => Promise<unknown>;

/** Проверка личности перед каждой попыткой; сервер также должен проверять ожидаемого владельца. */
export function siteTransport(scope: OfflineScope, request: Request = api): SiteTransport {
  return {
    async send(command): Promise<SiteTransportResult> {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15_000);
      try {
        const me = (await request('/auth/me', { signal: controller.signal })) as MeResponse;
        if (me?.user?.id !== scope.userId || me?.tenant?.id !== scope.tenantId || !me.tenant.staff)
          return { kind: 'auth_required' };
        if (!/^\/api\/v1\/site\/reports(?:\/[\da-f-]+(?:\/submit)?)?$/.test(command.path))
          return { kind: 'rejected', code: 'invalid_path', message: 'Недопустимый адрес команды площадки' };
        const body = JSON.parse(command.requestJson);
        const response = await request(command.path.slice('/api/v1'.length), {
          method: command.method,
          body,
          idempotencyKey: command.idempotencyKey,
          expectedIdentity: { userId: scope.userId, tenantId: scope.tenantId },
          signal: controller.signal,
        });
        const receipt = z
          .object({
            receiptId: z.uuid(),
            replayed: z.boolean(),
            result: z.object({ report: reportResponse }),
          })
          .parse(response);
        const report = receipt.result.report as SiteReportSnapshot;
        if (
          report.tenantId !== scope.tenantId ||
          report.authorId !== scope.userId ||
          report.clientId !== body.clientId ||
          (command.path.split('/')[5] !== undefined && command.path.split('/')[5] !== report.id) ||
          report.revision !== body.expectedRevision + 1
        )
          return { kind: 'retry' };
        return {
          kind: 'sent',
          ack: {
            clientId: report.clientId,
            clientOperationId: command.idempotencyKey,
            reportId: report.id,
            revision: report.revision,
          },
          snapshot: report,
        };
      } catch (error) {
        if (error instanceof ApiError) {
          if (
            error.status === 401 ||
            (error.status === 409 && error.code === 'session_context_changed') ||
            // «Глазами пользователя» (ДС-08): черновик не теряется — уйдёт после выхода из режима
            (error.status === 403 && error.code === 'view_as')
          )
            return { kind: 'auth_required' };
          // Ответ означает незавершённую первую попытку. Новый ключ здесь недопустим.
          if (error.status === 409 && error.code === 'command_in_progress') return { kind: 'retry' };
          if (error.status === 409 && error.code === 'revision_conflict') {
            const parsed = reportResponse.safeParse(
              (error.details as { server?: unknown } | undefined)?.server,
            );
            const body = JSON.parse(command.requestJson);
            if (
              parsed.success &&
              parsed.data.tenantId === scope.tenantId &&
              parsed.data.authorId === scope.userId &&
              parsed.data.clientId === body.clientId &&
              (command.path === '/api/v1/site/reports' || command.path.includes(`/${parsed.data.id}`))
            )
              return {
                kind: 'conflict',
                revision: parsed.data.revision,
                state: parsed.data.state,
                server: parsed.data,
              };
          }
          if (error.status >= 400 && error.status < 500 && error.status !== 429)
            return { kind: 'rejected', code: error.code, message: error.message };
        }
        return { kind: 'retry' };
      } finally {
        clearTimeout(timeout);
      }
    },
  };
}
